import { Receipt, CaretRight } from "@phosphor-icons/react";
import { money } from "@/lib/format";

// Always-visible one-line quote summary for the studio; click opens the full Live Quote panel.
// Shows the whole-quote total plus a per-area breakdown with the area being drawn highlighted.
export const LiveQuoteBar = ({ totals, areaId, material, edges, pieces, open, onOpen, showBar = true }) => {
  const total = totals?.total || 0;
  const areas = totals?.areas || [];
  const here = areas.find((a) => a.id === areaId);
  const stats = [
    [
      "This area",
      here ? money(here.subtotal) : "—",
      here ? `${here.sqft || 0} ft²` : "",
    ],
    [
      "Billed",
      totals ? `${totals.billed_sqft || 0} ft²` : "—",
      totals?.raw_sqft ? `${totals.raw_sqft} ft² drawn` : "",
    ],
    ["Material", material || "Not chosen", ""],
    ["Edges", edges || "—", ""],
    ["Pieces", String(pieces ?? 0), ""],
  ];
  return (
    <>
      {showBar && (
      <button
        type="button"
        data-testid="studio-quote-bar"
        onClick={onOpen}
        title="Open the full live quote"
        className={`group hidden w-full flex-col gap-2 rounded-xl border px-4 py-2.5 text-left transition-colors duration-150 lg:flex ${
          open
            ? "border-indigo-400/40 bg-indigo-500/10"
            : "border-zinc-800 bg-zinc-900/70 hover:border-indigo-400/40 hover:bg-zinc-900"
        }`}
      >
        <span className="flex w-full items-center gap-6">
          <span className="flex items-center gap-2.5">
            <Receipt size={16} weight="bold" className="text-indigo-300" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Live quote
            </span>
            <span
              className="font-mono-data text-lg font-bold tracking-tight text-zinc-50"
              data-testid="studio-quote-bar-total"
            >
              {money(total)}
            </span>
          </span>
          <span className="hidden h-6 w-px bg-zinc-800 xl:block" />
          <span className="flex min-w-0 flex-1 items-center gap-6">
            {stats.map(([k, v, sub]) => (
              <span
                key={k}
                className="min-w-0"
                data-testid={`studio-quote-bar-${k.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span className="block text-[9px] uppercase tracking-widest text-zinc-500">{k}</span>
                <span
                  className={`block truncate text-xs font-semibold ${
                    k === "This area" ? "font-mono-data text-amber-200" : "text-zinc-200"
                  }`}
                >
                  {v}
                  {sub && <span className="ml-1.5 font-normal text-zinc-500">· {sub}</span>}
                </span>
              </span>
            ))}
          </span>
          <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-indigo-300 group-hover:text-indigo-200">
            {open ? "Panel open" : "Details"} <CaretRight size={12} weight="bold" />
          </span>
        </span>
        {areas.length > 1 && (
          <span
            className="flex w-full flex-wrap items-center gap-1.5"
            data-testid="studio-quote-bar-areas"
          >
            {areas.map((a) => (
              <span
                key={a.id}
                data-testid={`studio-quote-bar-area-${a.id}`}
                data-current={a.id === areaId ? "1" : undefined}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] ${
                  a.id === areaId
                    ? "border-amber-400/60 bg-amber-400/10 text-amber-100"
                    : "border-zinc-800 text-zinc-400"
                }`}
              >
                <span className="truncate">{a.name}</span>
                <span className="font-mono-data font-semibold">{money(a.subtotal)}</span>
                <span className="text-zinc-500">{a.sqft || 0} ft²</span>
              </span>
            ))}
          </span>
        )}
      </button>
      )}

      <button
        type="button"
        data-testid="studio-quote-pill"
        onClick={onOpen}
        className="fixed bottom-4 right-4 z-30 flex items-center gap-2 rounded-full border border-indigo-400/40 bg-zinc-900/95 px-4 py-2.5 shadow-2xl backdrop-blur transition-colors duration-150 hover:bg-zinc-800 lg:hidden"
      >
        <Receipt size={15} weight="bold" className="text-indigo-300" />
        {here && areas.length > 1 ? (
          <>
            <span className="font-mono-data text-sm font-bold text-amber-200">{money(here.subtotal)}</span>
            <span className="text-[10px] text-zinc-500">of</span>
          </>
        ) : null}
        <span className="font-mono-data text-sm font-bold text-zinc-50">{money(total)}</span>
        <span className="text-[10px] text-zinc-400">{totals?.billed_sqft || 0} ft²</span>
      </button>
    </>
  );
};

export default LiveQuoteBar;
